import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { memo, useCallback } from "react";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { ViewTableMeta } from "@hooks/view/useViewTableMeta";
import { Button } from "@design-system/Button/Button";
import { HStack } from "@design-system/HStack/HStack";
import { PlusIcon } from "@icons";
import { TaskBlockedBadge } from "@components/tasks/TaskBlockedBadge/TaskBlockedBadge";
import { getBlockedByLabel } from "@components/tasks/taskBlockers";
import {
    CloseTemplateTaskRow,
    CloseTemplateTaskCallbacks,
} from "../closeTemplateTasksViewConfig";
import styles from "../CloseTemplateTasksDrawer.module.scss";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { BreakableText } from "designSystem/BreakableText/BreakableText";

type Meta = ViewTableMeta<CloseTemplateTaskCallbacks, never>;

export const CloseTemplateTaskNameCell = memo(
    function CloseTemplateTaskNameCell({
        row,
        getValue,
        table,
    }: CellContext<ViewTableFeatures, CloseTemplateTaskRow, string>) {
        const item = row.original;
        const cellValue = getValue();
        const meta = (table.options.meta ?? {}) as Meta;

        const handleAddTask = useCallback(
            (e: React.MouseEvent) => {
                e.stopPropagation();
                meta.callbacks?.onAddTaskForStage(item.closeStageId);
            },
            [meta.callbacks, item.closeStageId],
        );

        // Group header row
        if (item.isGroupRow) {
            return (
                <ViewTableCell captureClicks>
                    <div className={styles.groupRow}>
                        <span className={styles.groupTitle}>{item.name}</span>
                        <Tooltip content="Add task">
                            <Button
                                variant="secondary"
                                size="sm"
                                iconOnly
                                onClick={handleAddTask}
                                aria-label="Add task"
                            >
                                <PlusIcon />
                            </Button>
                        </Tooltip>
                    </div>
                </ViewTableCell>
            );
        }

        const blockers = item.blockedBy ?? [];
        const blockedByLabel = getBlockedByLabel({
            numberOfActiveBlockedByTasks: blockers.length,
            soleActiveBlockedByTask: blockers[0],
        });

        return (
            <ViewTableCell>
                <HStack as="span" align="center" gap={8}>
                    <BreakableText showTooltip text={cellValue} />
                    {blockers.length > 0 && (
                        <TaskBlockedBadge label={blockedByLabel} />
                    )}
                </HStack>
            </ViewTableCell>
        );
    },
);
